import React, { useState, useEffect, useCallback } from 'react';
import { searchUsersByEmail, addUserToFeatureGroup, getFeatureGroupMemberIds } from '../services/featureService';
import './AddUserPanel.css';

const AddUserPanel = ({ isOpen, featureGroupId, onClose, onSuccess }) => {
  const [mode, setMode] = useState('lookup'); // 'lookup' or 'invite'
  const [emailQuery, setEmailQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [role, setRole] = useState('Guest');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingMemberIds, setExistingMemberIds] = useState(new Set());

  const loadExistingMembers = useCallback(async () => {
    if (!featureGroupId) return;
    try {
      const memberIds = await getFeatureGroupMemberIds(featureGroupId);
      setExistingMemberIds(memberIds);
    } catch (err) {
      console.error('Error loading existing members:', err);
    }
  }, [featureGroupId]);

  const resetForm = () => {
    setMode('lookup');
    setEmailQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setInviteEmail('');
    setRole('Guest');
    setErrors({});
  };

  useEffect(() => {
    if (isOpen && featureGroupId) {
      loadExistingMembers();
      resetForm();
    }
  }, [isOpen, featureGroupId, loadExistingMembers]);

  const handleSearch = async () => {
    if (!emailQuery.trim()) {
      setErrors({ emailQuery: 'Please enter an email to search' });
      return;
    }

    setSearching(true);
    setErrors({});
    try {
      const results = await searchUsersByEmail(emailQuery);
      setSearchResults(results);
      if (results.length === 0) {
        setErrors({ emailQuery: 'No users found. Try inviting a new user instead.' });
      }
    } catch (err) {
      setErrors({ emailQuery: 'Error searching users' });
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    // Don't allow selection if user is already a member
    if (existingMemberIds.has(user.id)) {
      return;
    }
    setSelectedUser(user);
    setEmailQuery(user.email);
    setSearchResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !selectedUser) {
      e.preventDefault();
      handleSearch();
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (mode === 'lookup') {
      if (!selectedUser) {
        newErrors.user = 'Please select a user';
      }
    } else {
      if (!inviteEmail.trim()) {
        newErrors.inviteEmail = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
        newErrors.inviteEmail = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({});

    try {
      let userId = selectedUser?.id;
      
      // If inviting, we can't create users directly - they need to sign up first
      // In a production app, you would use Supabase Admin API to send invitation emails
      if (mode === 'invite') {
        setErrors({ 
          submit: 'User invitation requires Supabase Admin API. Please ask the user to sign up first, then use "Lookup Existing User" to add them.' 
        });
        setLoading(false);
        return;
      }

      // Add user to feature group with role
      await addUserToFeatureGroup(userId, featureGroupId, role);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to add user' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`add-user-panel ${isOpen ? 'open' : ''}`}>
      <div className="panel-content">
        <div className="panel-header">
          <h2>Add User</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="panel-body">
          <div className="mode-selector">
            <button
              type="button"
              className={`mode-button ${mode === 'lookup' ? 'active' : ''}`}
              onClick={() => {
                setMode('lookup');
                setSelectedUser(null);
                setInviteEmail('');
                setErrors({});
              }}
            >
              Lookup Existing User
            </button>
            <button
              type="button"
              className={`mode-button ${mode === 'invite' ? 'active' : ''}`}
              onClick={() => {
                setMode('invite');
                setSelectedUser(null);
                setEmailQuery('');
                setSearchResults([]);
                setErrors({});
              }}
            >
              Invite New User
            </button>
          </div>

          <form onSubmit={handleSubmit} className="add-user-form">
            {mode === 'lookup' ? (
              <div className="form-group">
                <label htmlFor="emailQuery">
                  Search by Email <span className="required">*</span>
                </label>
                <div className="search-input-group">
                  <input
                    id="emailQuery"
                    type="text"
                    value={emailQuery}
                    onChange={(e) => setEmailQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter email address..."
                    className={errors.emailQuery ? 'error' : ''}
                    disabled={!!selectedUser}
                  />
                  {!selectedUser && (
                    <button
                      type="button"
                      className="search-button"
                      onClick={handleSearch}
                      disabled={searching}
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </button>
                  )}
                  {selectedUser && (
                    <button
                      type="button"
                      className="clear-button"
                      onClick={() => {
                        setSelectedUser(null);
                        setEmailQuery('');
                        setSearchResults([]);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {errors.emailQuery && (
                  <span className="error-message">{errors.emailQuery}</span>
                )}
                {searchResults.length > 0 && !selectedUser && (
                  <div className="search-results">
                    {searchResults.map(user => {
                      const isExistingMember = existingMemberIds.has(user.id);
                      return (
                        <div
                          key={user.id}
                          className={`search-result-item ${isExistingMember ? 'disabled' : ''}`}
                          onClick={() => handleSelectUser(user)}
                          title={isExistingMember ? 'User is already a member of this feature group' : ''}
                        >
                          {user.email}
                          {isExistingMember && (
                            <span className="existing-member-badge">Already a member</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedUser && (
                  <div className="selected-user">
                    <i className="fas fa-check-circle"></i> Selected: {selectedUser.email}
                  </div>
                )}
                {errors.user && (
                  <span className="error-message">{errors.user}</span>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="inviteEmail">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className={errors.inviteEmail ? 'error' : ''}
                />
                {errors.inviteEmail && (
                  <span className="error-message">{errors.inviteEmail}</span>
                )}
                <small className="help-text">
                  An invitation will be sent to this email address
                </small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="role">
                Role <span className="required">*</span>
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={mode === 'lookup' ? !selectedUser : !inviteEmail.trim()}
              >
                <option value="Guest">Guest</option>
                <option value="Editor">Editor</option>
                <option value="Owner">Owner</option>
              </select>
              {(mode === 'lookup' && !selectedUser) && (
                <small className="help-text">Please select a user first</small>
              )}
              {(mode === 'invite' && !inviteEmail.trim()) && (
                <small className="help-text">Please enter an email first</small>
              )}
              {((mode === 'lookup' && selectedUser) || (mode === 'invite' && inviteEmail.trim())) && (
                <div className="role-description">
                  {role === 'Owner' && (
                    <small className="help-text">
                      Can control all aspects of this Feature Map including adding/inviting users.
                    </small>
                  )}
                  {role === 'Editor' && (
                    <small className="help-text">
                      Can add & edit features.
                    </small>
                  )}
                  {role === 'Guest' && (
                    <small className="help-text">
                      Can only view features, add ratings, votes and add comments.
                    </small>
                  )}
                </div>
              )}
            </div>

            {errors.submit && (
              <div className="error-message submit-error">{errors.submit}</div>
            )}

            <div className="panel-actions">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="save-button" disabled={loading}>
                {loading ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="panel-overlay" onClick={onClose}></div>
    </div>
  );
};

export default AddUserPanel;

